import { API_BASE_URL } from '@synergy/config';
import { FormattedMessage } from 'react-intl';
import { Locale } from '@synergy/models';
import { connect } from 'react-redux';
import { getLocalizedString, padZeroLeft } from '@synergy/utils';

import { AppState } from '../../../../store/models/app-state';
import { BaseAutocompleteSuggestionType } from '../../autocomplete-list/pool-autocomplete-list';
import { PoolFormAutocompleteList } from '../../abstract/form-autocomplete-list';
import { Product } from '../../../models/product';
import { ProductSearchApi } from '../../../../features/products/product/product-detail/variants/api/product-search-api';
import { localesSelector } from '../../../selectors/locale-selectors';

export interface PartialProduct extends BaseAutocompleteSuggestionType {
    name: string;
    supplierName: string;
}

interface PoolProductAutocompleteListProps {
    supplierIds?: number[];
    // When false, norm articles are excluded so only the given supplier's articles
    // are returned (invoice positions, #2097). Defaults to including them.
    includeNormArticles?: boolean;
    locales: Locale[];
}

const supplierKey = (supplierIds?: number[]): string => (supplierIds ?? []).join(',');

class PoolProductAutocompleteListBase extends PoolFormAutocompleteList<
    Product,
    PartialProduct,
    PoolProductAutocompleteListProps
> {
    // Keeps the supplier: this runs after every pick, and in "Mehrere
    // hinzufügen" the next search follows in the same open dialog — which then
    // offered the articles of every supplier again (#2210).
    public resetSearch = () => this.resetSearchParams(this.props.supplierIds);
    public label = this.props.label || <FormattedMessage id="app.label.product" />;
    public hideId = true;
    public alwaysShowSuggestions = false;
    private api = new ProductSearchApi(API_BASE_URL);
    private apiAbortController?: AbortController;
    private searchParams = new URLSearchParams({ limit: '40' });

    private resetSearchParams(supplierIds?: number[]) {
        this.searchParams = new URLSearchParams({ limit: '40' });
        if (supplierIds) {
            supplierIds.forEach(id => this.searchParams.append('supplierIds', id.toString()));
        }
        if (this.props.includeNormArticles === false) {
            this.searchParams.append('includeNormArticles', 'false');
        }
    }

    public componentDidMount() {
        this.setState({ items: [] });
        // Honour supplierIds already present at mount time. Without this, a list that
        // mounts with a stable supplierIds prop (e.g. the invoice positions step, where
        // the supplier is already chosen in step 1) would reset to an unfiltered search,
        // since the filter is otherwise only picked up reactively on prop change.
        this.resetSearchParams(this.props.supplierIds);
    }

    public UNSAFE_componentWillReceiveProps(nextProps: PoolProductAutocompleteListProps): void {
        // Which suppliers, not which array: every caller builds the list inline,
        // so comparing the reference made each render of the parent count as a
        // supplier change and throw away the results that had just arrived. The
        // search then answered "no articles found" over a full response (#2210).
        if (supplierKey(nextProps.supplierIds) !== supplierKey(this.props.supplierIds)) {
            this.resetSearchParams(nextProps.supplierIds);
            this.setState({ items: [] });
        }
    }

    public getSuggestionValue = (suggestion: PartialProduct) => suggestion && suggestion.name;

    public async handleValueChanged(search: string) {
        this.searchParams.set('search', search);

        this.apiAbortController?.abort();
        this.apiAbortController = new AbortController();
        const response = await this.api.search(this.searchParams, this.apiAbortController.signal);
        if (!response.success && response.requestCanceled) {
            return;
        }
        const items = response.success === true ? response.result.items : [];

        this.setState({ items });
    }

    public getPartial = (items: Product[]): PartialProduct[] => {
        return items.map(product => {
            return {
                id: padZeroLeft(product.id, 5),
                number: product.number || '',
                name: getLocalizedString(this.props.locales, product.name),
                supplierName: product.supplier?.name || '',
            };
        });
    };
}

const mapStateToProps = (state: AppState) => ({
    locales: localesSelector(state),
});

export const PoolProductAutocompleteList = connect(mapStateToProps, {})(PoolProductAutocompleteListBase);
